/**
 * Contact PM modal — used by enterprise customers to start a thread with
 * their assigned PM (or any unassigned PM as fallback). Persists into the
 * PM inbox via POST /api/admin/inbox/contact.
 */
import { useState } from "react";
import { X, Envelope } from "@phosphor-icons/react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

export default function ContactPMModal({ open, onClose, clientId }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  if (!open) return null;
  const submit = async () => {
    if (!body.trim()) { toast.error("Please write a message before sending."); return; }
    setSending(true);
    try {
      await api.post("/admin/inbox/contact", {
        subject: subject || `Inquiry · ${clientId || "client"}`,
        body,
      });
      toast.success("Sent to your PM — they'll be in touch shortly.");
      setSubject(""); setBody("");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send.");
    } finally {
      setSending(false);
    }
  };
  return (
    <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4" onClick={onClose} data-testid="contact-pm-modal">
      <div className="bg-[#0A0A0A] border-2 border-mango text-white max-w-xl w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-neutral-800">
          <div>
            <div className="overline text-mango flex items-center gap-2"><Envelope size={14} /> CONTACT YOUR PM</div>
            <h2 className="text-2xl font-black tracking-tight mt-1">New message</h2>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-white" data-testid="contact-pm-close"><X size={24} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="overline text-neutral-400">Subject (optional)</label>
            <input
              data-testid="contact-pm-subject"
              value={subject} onChange={(e) => setSubject(e.target.value)}
              placeholder="e.g. Need a new wave on FMCG sentiment"
              className="w-full bg-[#111] border border-neutral-700 px-3 py-2 text-white mt-1"
            />
          </div>
          <div>
            <label className="overline text-neutral-400">Message</label>
            <textarea
              data-testid="contact-pm-body"
              value={body} onChange={(e) => setBody(e.target.value)}
              rows={6} placeholder="Tell your PM what you need…"
              className="w-full bg-[#111] border border-neutral-700 px-3 py-2 text-white mt-1 font-mono text-sm"
            />
          </div>
          <div className="text-xs text-neutral-500">
            Your message will land in the PM inbox and your PM will be emailed.
            This is the official record — keep it concise.
          </div>
          <div className="flex justify-end gap-3 pt-2 border-t border-neutral-800">
            <Button variant="outline" onClick={onClose} className="border-neutral-700 text-neutral-300">Cancel</Button>
            <Button onClick={submit} disabled={sending} className="bg-mango hover:bg-mango/90 text-black font-bold" data-testid="contact-pm-send">
              {sending ? "Sending…" : "Send to PM"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
